import { useEffect, useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";

import FilterSearch from "../../../components/Filtering/FilterSearch";
import FilterRightBox from "../../../components/Filtering/FilterRightBoxOffering";
import BreadCrumb from "../../../components/HeaderComponents/BreadCrum";
import SearchButton from "../../../components/buttons/SearchButton";
import DataTable from "../../../components/DataTable/DataTable";
import CustomButton from "../../../components/buttons/CustomButton";

import { useFilteredData } from "../../../hooks/useFilteredData";
import { useTableControls } from "../../../hooks/useTableControls";
import { useAuthRoles } from "../../../hooks/useAuthRoles";
import { useGetDataService } from "../../../hooks/dataservice/useGetDataService";
import { useWorkerApiHelper } from "../../../hooks/useWorkerApiHelper";
import { formatDate } from "../../../utils/constants";
import { getTextByLang } from "../../../utils/formUtils";
import { extractIdFromIri } from "../../../utils/extractFromIri";
import { usePostDataService } from "../../../hooks/dataservice/usePostDataService";
import { usePutDataService } from "../../../hooks/dataservice/usePutDataService";
import { useDeleteDataService } from "../../../hooks/dataservice/useDeleteDataService";
import { showErrorAlert } from "../../../utils/showErrorAlert";
import AddIcon from "@mui/icons-material/Add";

import DataServiceForm from "./Crud/DataServiceForm";

const EMPTY_SERVES_DATASET = [];

const layoutByVariant = {
  page: {
    headerWidth: "w90",
    filtersWidth: "w86",
    tableWidth: "w90",
    breadcrumbInnerWidth: "w90",
    createButton: "w90",
    containerClass: "container containerflex",
  },
  embedded: {
    headerWidth: "W100",
    filtersWidth: "W100 plr2",
    tableWidth: "W100",
    breadcrumbInnerWidth: "W100",
    createButton: "W100",
    containerClass: "",
  },
};

const headerByVariant = {
  page: {
    Tag: "h2",
    titleClass: "tittlecatalog",
    titleKey: "mydataservice",
  },
  embedded: {
    Tag: "p",
    titleClass: "tittlecatalog--embedded",
    titleKey: "wizard.steps.dataset.dataservice.selectDataservice",
  },
};

export default function DataServiceTable({
  variant = "page",
  showHeader = variant === "page",
  showBreadcrumb = variant === "page",
  titleKey = "mydataservice",
  sectionClassName = "",
  canCreateOverride,
  canMutateOverride,
  showActionsOverride,
  selectionMode = "single",
  onSelectionChange,
}) {
  const { callApiWithWorker } = useWorkerApiHelper();
  const { t, i18n } = useTranslation();
  const { isProvider, isAdmin, decoded } = useAuthRoles();
  const participantId = String(decoded?.participant_id || "").trim();

  const layout = layoutByVariant[variant];
  const headerConfig = headerByVariant[variant];
  const canCreate =
    typeof canCreateOverride === "boolean" ? canCreateOverride : isProvider;
  const canMutate =
    typeof canMutateOverride === "boolean"
      ? canMutateOverride
      : isAdmin;
  const showActions =
    typeof showActionsOverride === "boolean" ? showActionsOverride : canMutate;

  const { Tag, titleClass, titleKey: resolvedTitleKey } = headerConfig;

  const [openDsDialog, setOpenDsDialog] = useState(false);
  const [selectedDataService, setSelectedDataService] = useState(null);
  const [loading, setLoading] = useState(false);

  const postDs = usePostDataService({ callApiWithWorker, t });
  const putDs = usePutDataService({ callApiWithWorker, t });
  const { mutateAsync: deleteDataService } = useDeleteDataService({
    callApiWithWorker,
    t,
  });

  const enableSelection = variant !== "page";
  const handleSelectedRowsChange = useCallback(
    (selectedRows, rowSelection) => {
      if (!enableSelection) return;
      const ids = Object.keys(rowSelection || {}).filter(
        (k) => rowSelection[k],
      );

      onSelectionChange?.(ids, selectedRows, rowSelection);
    },
    [enableSelection, onSelectionChange],
  );

  const {
    searchTerm,
    pendingSearchTerm,
    filters,
    open,
    pagination,
    rowCount,
    setPagination,
    setRowCount,
    handleSearch,
    handleClearSearch,
    handleSearchSubmit,
    handleSearchKeyDown,
    handleClose,
    handleApplyFilters,
  } = useTableControls([]);

  const handleOpenCreate = () => {
    setSelectedDataService(null);
    setOpenDsDialog(true);
  };

  const handleOpenEdit = (row) => {
    const id = extractIdFromIri(row.id);
    setSelectedDataService({ id });
    setOpenDsDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDsDialog(false);
    setSelectedDataService(null);
  };

  const handleSubmit = async (payload, { mode, initialDataService } = {}) => {
    try {
      if (mode === "edit") {
        const id = initialDataService?.id ?? selectedDataService?.id;
        if (!id) throw new Error("Missing DataService id for edit");

        const {
          id: _id,
          identifier: _identifier,
          ...cleanPayload
        } = payload && typeof payload === "object" ? payload : {};

        await putDs.mutateAsync({ id, payload: cleanPayload });
      } else {
        await postDs.mutateAsync(payload);
      }

      handleCloseDialog();
    } catch (err) {
      showErrorAlert(err);
    }
  };

  const handleDelete = async (identifier) => {
    const id = extractIdFromIri(identifier);
    try {
      setLoading(true);
      await deleteDataService({ id });
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        header: t("wizard.steps.dataset.dataservice.updateDate"),
        accessorKey: "updated_at",
        meta: { width: "15%" },
        cell: ({ getValue }) => formatDate(getValue()),
      },
      {
        header: t("wizard.steps.dataset.dataservice.name"),
        accessorKey: "title",
        meta: { width: "35%" },
      },
      {
        header: t("wizard.steps.dataset.dataservice.description"),
        accessorKey: "description",
        meta: { width: "45%" },
      },
    ],
    [t],
  );

  const { data: pageResponse } = useGetDataService({
    callApiWithWorker,
    language: i18n.language,
    pageIndex: pagination.pageIndex,
    pageSize: pagination.pageSize,
  });

  const rows = useMemo(() => {
    const content = pageResponse?.content ?? [];
    return content.map((item) => ({
      ...item,
      publisher: canMutate && participantId ? participantId : item.publisher,
      assigner: canMutate && participantId ? participantId : item.assigner,
      title: getTextByLang(item.title, i18n.language),
      description: getTextByLang(item.description, i18n.language),
    }));
  }, [pageResponse, i18n.language, canMutate, participantId]);

  useEffect(() => {
    setRowCount(pageResponse?.totalElements ?? 0);
  }, [pageResponse?.totalElements, setRowCount]);

  const filteredData = useFilteredData(rows, searchTerm, filters, columns);

  return (
    <section className={sectionClassName}>
      {showBreadcrumb && (
        <div className="w100 flexbread">
          <div className={`divbreadcrum ${layout.breadcrumbInnerWidth}`}>
            <BreadCrumb />
          </div>
        </div>
      )}

      <div className={layout.containerClass}>
        {showHeader && (
          <div className={`${layout.headerWidth} labeltittle`}>
            <Tag className={titleClass}>{t(resolvedTitleKey)}</Tag>

            {canCreate && (
              <CustomButton onClick={handleOpenCreate} startIcon={<AddIcon />}>
                {t("wizard.steps.dataset.dataservice.create")}
              </CustomButton>
            )}
          </div>
        )}

        <div className={`${layout.filtersWidth} filteractions pt3`}>
          <FilterSearch
            value={pendingSearchTerm}
            onChange={handleSearch}
            onClear={handleClearSearch}
            onKeyDown={handleSearchKeyDown}
          />
          <div className="plr2">
            <SearchButton onClick={handleSearchSubmit} />
          </div>
        </div>

        <FilterRightBox
          open={open}
          onClose={handleClose}
          onApplyFilters={handleApplyFilters}
        />

        <div className={`${layout.tableWidth} bgwhite mtb2`}>
          <div className="plr3 ptb">
            <DataTable
              data={filteredData}
              columns={columns}
              showActions={showActions}
              onEdit={canMutate ? (row) => handleOpenEdit(row) : undefined}
              onDelete={canMutate ? (row) => handleDelete(row.id) : undefined}
              initialSorting={[{ id: "updated_at", desc: true }]}
              pagination={pagination}
              setPagination={setPagination}
              rowCount={rowCount}
              {...(enableSelection
                ? {
                    enableRowSelection: true,
                    selectionMode,
                    getRowId: (row) => String(row.id),
                    onSelectedRowsChange: handleSelectedRowsChange,
                    onRowClick: undefined,
                  }
                : {})}
            />
          </div>
        </div>

        <DataServiceForm
          open={openDsDialog}
          onClose={handleCloseDialog}
          onSubmit={handleSubmit}
          loading={loading}
          t={t}
          i18n={i18n}
          initialDataService={selectedDataService}
          servesDataset={EMPTY_SERVES_DATASET}
          includeIdentifiersInPayload={false}
          enableDirtyCheck={true}
        />
      </div>
    </section>
  );
}
